import { useState, useEffect, useRef } from 'react';
import { getForResponse, submitAnswer } from '../api/mentimeterApi';
import { resolveImageUrl } from '../utils/resolveImageUrl';

export default function MentimeterResponsePage({ branchCode, divisionCode, mentiYear, seqNo }) {
  const [data, setData]           = useState(null);
  const [currentQ, setCurrentQ]   = useState(0);
  const [answers, setAnswers]     = useState({});
  const [submitted, setSubmitted] = useState({});  // 질문별 제출 여부
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const prevQRef                  = useRef(0);

  // 초기 로드
  useEffect(() => {
    const load = async () => {
      const res = await getForResponse(branchCode, divisionCode, mentiYear, seqNo);
      if (res.success) {
        setData(res.data);
        setCurrentQ(res.data.current_q_no || 0);
      } else {
        setError(res.message);
      }
    };
    load();
  }, [mentiYear, seqNo]);

  // 폴링 — current_q_no 감지
  useEffect(() => {
    if (!data) return;
    const poll = async () => {
      const res = await getForResponse(branchCode, divisionCode, mentiYear, seqNo);
      if (res.success) {
        const newQ = res.data.current_q_no || 0;
        setCurrentQ(newQ);
        setData(res.data);
      }
    };
    const timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  }, [data, branchCode, divisionCode, mentiYear, seqNo]);

  // 질문 전환 시 스크롤 상단
  useEffect(() => {
    if (currentQ !== prevQRef.current) {
      prevQRef.current = currentQ;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentQ]);

  const setAnswer = (q_no, val) => setAnswers(p => ({ ...p, [q_no]: val }));

  const handleSubmitOne = async (q_no) => {
    setError('');
    if (!answers[q_no]) { setError('답변을 선택해주세요.'); return; }
    setLoading(true);
    try {
      const res = await submitAnswer(branchCode, divisionCode, mentiYear, seqNo, [{ q_no, answer: answers[q_no] }]);
      if (res.success) setSubmitted(p => ({ ...p, [q_no]: true }));
      else setError(res.message || '제출에 실패했습니다. 다시 시도해주세요.');
    } catch (err) {
      setError('네트워크가 혼잡합니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  if (error && !data) return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#ff4d4f' }}>⚠️ {error}</div>
      </div>
    </div>
  );

  if (!data) return (
    <div style={S.wrap}>
      <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>로딩 중...</div>
    </div>
  );

  if (currentQ === 0) return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{data.title}</h2>
          <p style={{ fontSize: 14, color: '#888' }}>잠시 후 질문이 시작됩니다...</p>
          <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'center' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1677ff', animation: 'pulse 1s infinite' }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1677ff', animation: 'pulse 1s infinite 0.2s' }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1677ff', animation: 'pulse 1s infinite 0.4s' }} />
          </div>
        </div>
      </div>
    </div>
  );

  const q = data.questions?.find(q => q.q_no === currentQ);
  if (!q) return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>모든 질문이 완료되었습니다!</h2>
          <p style={{ fontSize: 14, color: '#888' }}>소중한 의견 감사합니다 😊</p>
        </div>
      </div>
    </div>
  );

  const isSubmitted = submitted[q.q_no];

    // 질문 번호 생성용 핸들러
  const getDisplayQNo = (targetQ) => {
    if (!data?.questions) return targetQ.q_no;
    const answerableList = data.questions.filter(r => r.q_type !== 'I');
    const idx = answerableList.findIndex(r => r.q_no === targetQ.q_no);
    return idx + 1;
  };

  // 진행률 생성용 핸들러
  const getProgressLabel = () => {
    if (!data?.questions) return `${currentQ} / ${data.questions?.length || 0}`;
    const answerableList = data.questions.filter(r => r.q_type !== 'I');
    const totalReal = answerableList.length;
    if (q.q_type === 'I') return `전체 ${totalReal}문항`;
    const idx = answerableList.findIndex(r => r.q_no === q.q_no);
    return `${idx + 1} / ${totalReal}`;
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={{ fontSize: 24, marginBottom: 6 }}>📋</div>
        <h1 style={S.title}>{data.title}</h1>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
          {getProgressLabel()}
        </div>
      </div>

      <div style={S.card}>
        {/* 이미지 */}
        {q.img_data && (
          <img src={resolveImageUrl(q.img_data)} alt="질문이미지"
              style={{ width: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 300, objectFit: 'contain' }} />
        )}

        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>
          {q.q_type === 'I' ? q.q_title : `Q${getDisplayQNo(q)}. ${q.q_title}`}
        </div>

        {q.q_type === 'I' ? null : isSubmitted ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#52c41a' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 700 }}>응답 완료!</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>다음 질문을 기다려주세요...</div>
          </div>
        ) : (
          <>
            {/* 객관식 */}
            {q.q_type === 'M' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {q.options.map(o => (
                  <button key={o.opt_no}
                    style={{ ...S.optBtn, background: answers[q.q_no] === o.opt_name ? '#1677ff' : '#f5f5f5', color: answers[q.q_no] === o.opt_name ? '#fff' : '#333' }}
                    onClick={() => setAnswer(q.q_no, o.opt_name)}>
                    {o.opt_name}
                  </button>
                ))}
              </div>
            )}

            {/* 단답형 */}
            {q.q_type === 'T' && (
              <input style={S.input}
                value={answers[q.q_no] || ''}
                onChange={e => setAnswer(q.q_no, e.target.value)}
                maxLength={30}
                placeholder="단어나 짧은 문장으로 입력해주세요 (30자 이내)" />
            )}

            {/* OX */}
            {q.q_type === 'O' && (
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                {['O', 'X'].map(v => (
                  <button key={v}
                    style={{ ...S.oxBtn, background: answers[q.q_no] === v ? (v === 'O' ? '#52c41a' : '#ff4d4f') : '#f5f5f5', color: answers[q.q_no] === v ? '#fff' : '#333' }}
                    onClick={() => setAnswer(q.q_no, v)}>
                    {v === 'O' ? '⭕' : '❌'}
                  </button>
                ))}
              </div>
            )}

            {/* 순위형 */}
            {q.q_type === 'R' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {q.options.map(o => (
                  <button key={o.opt_no}
                    style={{ ...S.optBtn, background: answers[q.q_no] === o.opt_name ? '#722ed1' : '#f5f5f5', color: answers[q.q_no] === o.opt_name ? '#fff' : '#333' }}
                    onClick={() => setAnswer(q.q_no, o.opt_name)}>
                    {o.opt_name}
                  </button>
                ))}
              </div>
            )}

            {/* 평점 */}
            {q.q_type === 'S' && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {[1, 2, 3, 4, 5].map(v => (
                  <button key={v}
                    style={{ fontSize: 36, background: 'none', border: 'none', cursor: 'pointer', opacity: answers[q.q_no] >= v ? 1 : 0.3, transform: answers[q.q_no] >= v ? 'scale(1.1)' : 'scale(1)', transition: 'all 0.1s' }}
                    onClick={() => setAnswer(q.q_no, String(v))}>
                    ⭐
                  </button>
                ))}
              </div>
            )}

            {error && <div style={S.error}>⚠️ {error}</div>}

            <button style={{ ...S.submitBtn, marginTop: 20, opacity: loading ? 0.7 : 1 }}
              onClick={() => handleSubmitOne(q.q_no)} disabled={loading}>
              {loading ? '제출 중...' : '답변 제출'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  wrap      : { padding: 16, maxWidth: 520, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5' },
  header    : { background: 'linear-gradient(135deg, #1677ff, #4096ff)', borderRadius: 16, padding: '24px 20px', marginBottom: 16, textAlign: 'center' },
  title     : { fontSize: 20, fontWeight: 800, color: '#fff', margin: 0 },
  card      : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  optBtn    : { border: 'none', borderRadius: 10, padding: '12px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' },
  oxBtn     : { border: 'none', borderRadius: 16, width: 100, height: 100, fontSize: 40, cursor: 'pointer', transition: 'all 0.15s' },
  input     : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  error     : { background: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c0392b', marginBottom: 12, marginTop: 12 },
  submitBtn : { width: '100%', background: 'linear-gradient(135deg, #1677ff, #4096ff)', color: '#fff', border: 'none', borderRadius: 12, padding: 16, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
};